import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, CheckCircle2, XCircle, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function GeoTagRequestsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending_confirmation");
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/geo-tag-requests", { params: { status, page_size: 50 } });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="geo-tag-requests-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Field ops" }, { label: "Geo-tag approvals" }]}
        title="Geo-tag approvals"
        subtitle="Field-submitted outlet geo-tag corrections awaiting your approval."
        favorite={{ key: "admin.geo-tag", type: "screen", label: "Geo-tag approvals", route: "/manager/geo-tag-requests" }}
        testid="geotagrequestspage-page-header"
      />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Geo-tag approvals</h1>
          <p className="text-sm text-neutral-500">Confirm GPS coordinates captured on first visit before updating outlet master.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="geo-tag-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending_confirmation">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="space-y-2" data-testid="geo-tag-list">
        {items.length === 0 && !loading && (
          <div className="py-10 text-center text-neutral-400">No requests</div>
        )}
        {items.map((v) => (
          <Card key={v.id} className="border-neutral-200 rounded-2xl"
                data-testid={`geo-tag-card-${v.id.slice(0, 8)}`}>
            <CardContent className="p-4 flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="text-[11px] uppercase tracking-widest text-neutral-400">
                  {v.retailer?.code || "—"}
                </div>
                <div className="text-base font-bold text-neutral-900">{v.retailer?.name || "—"}</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {v.retailer?.address || "no address"}
                </div>
                <div className="text-xs text-neutral-600 mt-2 flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  <span className="font-mono">
                    {v.captured_outlet_gps?.lat?.toFixed(6)}, {v.captured_outlet_gps?.lng?.toFixed(6)} · ±{v.captured_outlet_gps?.accuracy_m || "?"}m
                  </span>
                </div>
                <div className="text-[11px] text-neutral-400 mt-1">
                  Captured by {v.user?.name || v.user_id?.slice(0, 8)} at{" "}
                  {new Date(v.check_in_ts).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                </div>
                {v.captured_outlet_gps_decision_notes && (
                  <div className="text-[12px] text-neutral-500 mt-1 italic">
                    "{v.captured_outlet_gps_decision_notes}"
                  </div>
                )}
              </div>
              {v.captured_outlet_gps_status === "pending_confirmation" ? (
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setDecision({ visit: v, action: "reject" })}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`geo-tag-reject-${v.id.slice(0, 8)}`}>
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                  <Button onClick={() => setDecision({ visit: v, action: "approve" })}
                          className="tulsi-gradient text-white"
                          data-testid={`geo-tag-approve-${v.id.slice(0, 8)}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                  </Button>
                </div>
              ) : (
                <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                  v.captured_outlet_gps_status === "approved" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"
                }`}>{v.captured_outlet_gps_status.replace(/_/g, " ")}</span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {decision && (
        <DecisionDialog visit={decision.visit} action={decision.action}
                        onClose={() => setDecision(null)}
                        onDone={() => { setDecision(null); load(); }} />
      )}
    </div>
  );
}

function DecisionDialog({ visit, action, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const isReject = action === "reject";

  const submit = async () => {
    if (isReject && !notes.trim()) return toast.error("Rejection notes required");
    setBusy(true);
    try {
      await api.post(`/geo-tag-requests/${visit.id}/${action}`, { notes: notes.trim() || null });
      toast.success(isReject ? "Rejected" : "Geo-tag approved");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="geo-tag-decision-dialog">
        <DialogHeader>
          <DialogTitle>{isReject ? "Reject geo-tag" : "Approve geo-tag"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-neutral-600">
            {visit.retailer?.name} — <span className="font-mono text-xs">
              {visit.captured_outlet_gps?.lat?.toFixed(5)}, {visit.captured_outlet_gps?.lng?.toFixed(5)}
            </span>
          </div>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder={isReject ? "Reason for rejection…" : "Optional notes"}
                    data-testid="geo-tag-decision-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className={isReject ? "bg-red-600 text-white hover:bg-red-700" : "tulsi-gradient text-white"}
                  data-testid="geo-tag-decision-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isReject ? "Reject" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
